import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// This is the exact BFF Classic v3 stadium serialized in replay statistics.
// Keeping the extracted map separate from the older clean-room fixture lets
// replay analysis and Gym target the same geometry/traits without changing
// existing fixture contracts.
import { bffClassicV3 } from '../vendor/bffClassicV3';
import { analyzeDecision } from '../utils/liveAnalysis';
import { createGymNativeEngine } from '../utils/nativeGymPhysics';
import './gym.scss';

const HUMAN = 1;
const OPPONENT = 2;
const TICK_RATE = 60;
const STORAGE_KEY = 'hax-gym-training-v1';
// HaxBall's extrapolation setting is a render-time prediction window. It must
// never mutate authoritative physics or controls: the fixed tick state remains
// the source of truth and only the canvas presentation is moved ahead.
const EXTRAPOLATION_LIMIT_MS = 200;

function extrapolateState(state, milliseconds, nativeEngine) {
  const ms = Math.max(-EXTRAPOLATION_LIMIT_MS, Math.min(EXTRAPOLATION_LIMIT_MS, Number(milliseconds) || 0));
  if (!ms) return state;
  const ticks = Math.round(ms * TICK_RATE / 1000);
  // Positive windows use the same fixed-step solver as gameplay, carrying the
  // last observed input for each player. This includes wall/player collisions;
  // it is closer to native HaxBall extrapolation than a straight-line overlay.
  if (ticks > 0 && nativeEngine) return nativeEngine.predict(ms);
  // A negative setting asks the renderer to look behind the latest tick. The
  // native client cannot replay unknown historical inputs here, so use a
  // bounded velocity rewind while leaving authoritative state untouched.
  const rewindTicks = ticks;
  const advance = disc => ({
    ...disc,
    position: {
      x: disc.position.x + disc.velocity.x * rewindTicks,
      y: disc.position.y + disc.velocity.y * rewindTicks
    }
  });
  return { ...state, players: state.players.map(advance), ball: advance(state.ball) };
}

function expandClassic() {
  const classic = bffClassicV3;
  const traits = classic.traits || {};
  const mergeTrait = segment => ({ ...((segment.trait && traits[segment.trait]) || {}), ...segment });
  const sourceBall = classic.ballPhysics === 'disc0'
    ? classic.discs?.[0]
    : classic.ballPhysics;
  return {
    ...classic,
    // Keep the replay's exact stadium identity.  The Gym route is an isolated
    // client, but the native parser must see the same map name and constants
    // that were serialized in the source replay.
    name: classic.name,
    sourceName: classic.name,
    width: classic.width,
    height: classic.height,
    segments: classic.segments.map(mergeTrait),
    planes: classic.planes.map(mergeTrait),
    vertexes: classic.vertexes.map(mergeTrait),
    discs: classic.discs.slice(classic.ballPhysics === 'disc0' ? 1 : 0).map(mergeTrait),
    ballPhysics: sourceBall ? mergeTrait(sourceBall) : undefined,
    // Preserve the replay's complete player physics block.  In particular,
    // damping, kick damping, collision group, and gravity must reach h.Lk
    // unchanged for frame-for-frame parity with the source replay.
    playerPhysics: { ...(classic.playerPhysics || {}) }
  };
}

const STADIUM = expandClassic();

function initialState() {
  const ballPhysics = STADIUM.ballPhysics || {};
  const spawnDistance = Number(STADIUM.spawnDistance) || 200;
  return {
    tick: 0,
    stadium: STADIUM,
    ball: { position: { x: 0, y: 0 }, velocity: { x: 0, y: 0 }, radius: Number(ballPhysics.radius) || 5.8, invMass: Number(ballPhysics.invMass) || 1.55, bCoef: Number(ballPhysics.bCoef) || .412, damping: Number(ballPhysics.damping) || .99, gravity: { x: 0, y: 0 }, cMask: 63, cGroup: 193 },
    players: [
      { id: HUMAN, name: 'You', team: 'red', position: { x: -spawnDistance, y: 0 }, velocity: { x: 0, y: 0 }, radius: 15, invMass: .5, damping: .96, cMask: 39, cGroup: 2, kickCooldown: 0, active: true },
      { id: OPPONENT, name: 'BFF model', team: 'blue', position: { x: spawnDistance, y: 0 }, velocity: { x: 0, y: 0 }, radius: 15, invMass: .5, damping: .96, cMask: 39, cGroup: 4, kickCooldown: 0, active: true }
    ]
  };
}

function directionForKeys(keys) {
  const x = (keys.has('ArrowRight') || keys.has('d') ? 1 : 0) - (keys.has('ArrowLeft') || keys.has('a') ? 1 : 0);
  const y = (keys.has('ArrowDown') || keys.has('s') ? 1 : 0) - (keys.has('ArrowUp') || keys.has('w') ? 1 : 0);
  return { x, y };
}

function aiControls(state, skill, policy) {
  const player = state.players.find(p => p.id === OPPONENT);
  const ball = state.ball.position;
  if (!player) return {};
  const dx = ball.x - player.position.x;
  const dy = ball.y - player.position.y;
  const distance = Math.hypot(dx, dy) || 1;
  // Blue attacks the left goal. It approaches the far side of the ball so
  // kicks follow the native player-to-ball direction rather than teleporting.
  const goalDirection = { x: -1, y: 0 };
  const behind = { x: ball.x - goalDirection.x * 30, y: ball.y - goalDirection.y * 30 };
  const targetDx = behind.x - player.position.x;
  const targetDy = behind.y - player.position.y;
  const targetDistance = Math.hypot(targetDx, targetDy) || 1;
  const input = targetDistance > 24 / Math.max(.5, Number(policy?.pursuit ?? 1)) ? { x: targetDx / targetDistance, y: targetDy / targetDistance } : { x: dx / distance, y: dy / distance };
  const contact = distance - player.radius - state.ball.radius < 4;
  const kickRange = 300 + Number(policy?.kickRange ?? 0);
  const kick = contact && player.kickCooldown <= 0 && (skill === 'pressing' || Math.abs(ball.x) < kickRange);
  return { [OPPONENT]: { input, kick } };
}

function resetPositions(state) {
  const next = initialState();
  next.tick = state.tick;
  return next;
}

function worldToCanvas(point, width, height, map = STADIUM) {
  const scale = Math.min(width / (map.width * 2), height / (map.height * 2));
  return { x: width / 2 + point.x * scale, y: height / 2 + point.y * scale, scale };
}

function drawGym(ctx, state, prediction, stats) {
  // The backing canvas is scaled for device pixel ratio by resizeCanvas. Use
  // its CSS dimensions for world coordinates so Retina screens do not zoom
  // the pitch outside the visible canvas.
  const width = ctx.canvas.clientWidth || ctx.canvas.width;
  const height = ctx.canvas.clientHeight || ctx.canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#08121d'; ctx.fillRect(0, 0, width, height);
  const map = state.stadium || STADIUM;
  const scale = Math.min(width / (map.width * 2), height / (map.height * 2));
  const bgWidth = Number(map.bg?.width) || map.width;
  const bgHeight = Number(map.bg?.height) || map.height;
  const left = width / 2 - bgWidth * scale, top = height / 2 - bgHeight * scale;
  ctx.fillStyle = '#0d5b46'; ctx.fillRect(left, top, bgWidth * 2 * scale, bgHeight * 2 * scale);
  ctx.strokeStyle = 'rgba(190,255,228,.7)'; ctx.lineWidth = 2; ctx.strokeRect(left, top, bgWidth * 2 * scale, bgHeight * 2 * scale);
  ctx.beginPath(); ctx.moveTo(width / 2, top); ctx.lineTo(width / 2, top + bgHeight * 2 * scale); ctx.stroke();
  ctx.beginPath(); ctx.arc(width / 2, height / 2, (Number(map.bg?.kickOffRadius) || 60) * scale, 0, Math.PI * 2); ctx.stroke();
  // Draw the same serialized wall/goal geometry that the native stadium
  // parser feeds to the collision solver. The field fill remains a readable
  // backdrop, while these segments expose the real futsal goal channels and
  // kickoff walls instead of implying a generic rectangular pitch.
  if (Array.isArray(map.vertexes) && Array.isArray(map.segments)) {
    ctx.save();
    map.segments.filter(segment => segment.vis !== false).forEach(segment => {
      const start = map.vertexes[segment.v0];
      const end = map.vertexes[segment.v1];
      if (!start || !end) return;
      const a = worldToCanvas(start, width, height, map);
      const b = worldToCanvas(end, width, height, map);
      ctx.beginPath();
      ctx.strokeStyle = segment.color ? `#${String(segment.color).replace(/^#/, '').padStart(6, '0')}` : 'rgba(220,240,235,.76)';
      ctx.lineWidth = segment.cMask?.includes('ball') ? 2 : 1;
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    });
    ctx.restore();
  }
  if (Array.isArray(map.discs)) {
    map.discs.forEach(disc => {
      const point = worldToCanvas(disc.pos || disc.position, width, height, map);
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
      ctx.beginPath(); ctx.fillStyle = disc.color ? `#${String(disc.color).replace(/^#/, '').padStart(6, '0')}` : '#abc2d5';
      ctx.arc(point.x, point.y, Number(disc.radius || 5) * point.scale, 0, Math.PI * 2); ctx.fill();
    });
  }
  ctx.fillStyle = '#d9f8ed'; ctx.font = '12px system-ui'; ctx.fillText('BFF CLASSIC · 1v1', left + 14, top + 20);
  const drawPath = (path, colour, dashed = false) => {
    if (!Array.isArray(path) || path.length < 2) return;
    ctx.save(); ctx.strokeStyle = colour; ctx.lineWidth = 3; ctx.setLineDash(dashed ? [7, 6] : []); ctx.beginPath();
    path.forEach((point, index) => { const p = worldToCanvas(point, width, height, map); index ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y); });
    ctx.stroke(); ctx.restore();
  };
  if (prediction?.best?.path) drawPath(prediction.best.path, '#ffd166', true);
  state.players.forEach(player => {
    const point = worldToCanvas(player.position, width, height, map);
    ctx.beginPath(); ctx.fillStyle = player.id === HUMAN ? '#54edbf' : '#ff7285'; ctx.arc(point.x, point.y, 15 * point.scale, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#07121b'; ctx.font = 'bold 11px system-ui'; ctx.textAlign = 'center'; ctx.fillText(player.id === HUMAN ? 'YOU' : 'AI', point.x, point.y + 4); ctx.textAlign = 'left';
  });
  const ball = worldToCanvas(state.ball.position, width, height, map);
  ctx.beginPath(); ctx.fillStyle = '#f7fbff'; ctx.arc(ball.x, ball.y, 6 * ball.scale, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(220,240,235,.72)'; ctx.font = '12px system-ui'; ctx.fillText(`tick ${state.tick} · ${stats.you}–${stats.ai}`, left + 14, top + 38);
}

export default function GymApp() {
  const canvasRef = useRef(null);
  const stateRef = useRef(initialState());
  const nativeEngineRef = useRef(null);
  if (!nativeEngineRef.current) nativeEngineRef.current = createGymNativeEngine(STADIUM, stateRef.current);
  const keysRef = useRef(new Set());
  const rafRef = useRef(null);
  const accumulatorRef = useRef(0);
  const lastTimeRef = useRef(0);
  const examplesRef = useRef([]);
  const [running, setRunning] = useState(false);
  const [skill, setSkill] = useState(() => localStorage.getItem(`${STORAGE_KEY}:skill`) || 'balanced');
  const [extrapolationMs, setExtrapolationMs] = useState(() => {
    const saved = Number(localStorage.getItem(`${STORAGE_KEY}:extrapolationMs`));
    return Number.isFinite(saved) ? Math.max(-EXTRAPOLATION_LIMIT_MS, Math.min(EXTRAPOLATION_LIMIT_MS, saved)) : 0;
  });
  const [policy, setPolicy] = useState(() => { try { return JSON.parse(localStorage.getItem(`${STORAGE_KEY}:policy`) || '{"version":0,"kickRange":0,"pursuit":1}'); } catch (_) { return { version: 0, kickRange: 0, pursuit: 1 }; } });
  const [stats, setStats] = useState({ you: 0, ai: 0, episodes: 0, examples: Number(localStorage.getItem(`${STORAGE_KEY}:examples`) || 0), trained: Number(localStorage.getItem(`${STORAGE_KEY}:trained`) || 0) });
  const [prediction, setPrediction] = useState(null);

  const reset = useCallback(() => {
    const next = initialState();
    nativeEngineRef.current.reset(next);
    stateRef.current = nativeEngineRef.current.getState();
    setPrediction(null);
    setRunning(false);
  }, []);
  const scoreGoal = useCallback(team => {
    setStats(old => ({ ...old, you: old.you + (team === 'red' ? 1 : 0), ai: old.ai + (team === 'blue' ? 1 : 0), episodes: old.episodes + 1 }));
    nativeEngineRef.current.reset(resetPositions(stateRef.current));
    stateRef.current = nativeEngineRef.current.getState();
  }, []);

  useEffect(() => {
    const down = event => { if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', ' '].includes(event.key)) { event.preventDefault(); keysRef.current.add(event.key); } };
    const up = event => keysRef.current.delete(event.key);
    window.addEventListener('keydown', down); window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  useEffect(() => {
    const loop = timestamp => {
      const delta = Math.min(100, lastTimeRef.current ? timestamp - lastTimeRef.current : 16.67); lastTimeRef.current = timestamp;
      if (running) {
        accumulatorRef.current += delta;
        while (accumulatorRef.current >= 1000 / TICK_RATE) {
          const state = stateRef.current;
          const input = directionForKeys(keysRef.current);
          const controls = { [HUMAN]: { input, kick: keysRef.current.has(' ') }, ...aiControls(state, skill, policy) };
          stateRef.current = nativeEngineRef.current.step(controls).state;
          const ball = stateRef.current.ball.position;
          const redGoalX = Number(STADIUM.goals?.find(goal => goal.team === 'red')?.p0?.[0] ?? -STADIUM.width);
          const blueGoalX = Number(STADIUM.goals?.find(goal => goal.team === 'blue')?.p0?.[0] ?? STADIUM.width);
          if (ball.x < redGoalX) { scoreGoal('blue'); break; }
          if (ball.x > blueGoalX) { scoreGoal('red'); break; }
          if (stateRef.current.tick % 30 === 0) {
            examplesRef.current.push({ tick: stateRef.current.tick, state: { ball: stateRef.current.ball.position, player: stateRef.current.players[0].position }, input, reward: 0 });
            if (examplesRef.current.length > 10000) examplesRef.current.shift();
          }
          accumulatorRef.current -= 1000 / TICK_RATE;
        }
      }
      const canvas = canvasRef.current; if (canvas) drawGym(canvas.getContext('2d'), extrapolateState(stateRef.current, extrapolationMs, nativeEngineRef.current), prediction, stats);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [extrapolationMs, policy, prediction, running, scoreGoal, skill, stats]);

  const runPrediction = () => {
    try { setPrediction(analyzeDecision(stateRef.current, HUMAN, { allowClassic: true })); } catch (_) { setPrediction(null); }
  };
  const train = () => {
    const count = examplesRef.current.length;
    if (!count) return;
    const trained = stats.trained + 1;
    const examples = stats.examples + count;
    const reward = stats.ai - stats.you;
    const nextPolicy = {
      version: Number(policy.version || 0) + 1,
      kickRange: Math.max(-120, Math.min(120, Number(policy.kickRange || 0) + reward * 8)),
      pursuit: Math.max(.75, Math.min(1.25, Number(policy.pursuit || 1) + reward * .015))
    };
    localStorage.setItem(`${STORAGE_KEY}:examples`, String(examples)); localStorage.setItem(`${STORAGE_KEY}:trained`, String(trained));
    localStorage.setItem(`${STORAGE_KEY}:skill`, skill); localStorage.setItem(`${STORAGE_KEY}:policy`, JSON.stringify(nextPolicy));
    setPolicy(nextPolicy); setStats(old => ({ ...old, examples, trained })); examplesRef.current = [];
  };
  const resizeCanvas = canvas => { if (!canvas) return; const rect = canvas.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1; canvas.width = Math.round(rect.width * dpr); canvas.height = Math.round(rect.height * dpr); canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0); };
  useEffect(() => {
    const canvas = canvasRef.current; resizeCanvas(canvas);
    if (typeof ResizeObserver === 'undefined' || !canvas) return undefined;
    const observer = new ResizeObserver(() => resizeCanvas(canvas)); observer.observe(canvas); return () => observer.disconnect();
  }, []);

  const bestText = useMemo(() => prediction?.best?.label || prediction?.best?.type || 'No legal move yet', [prediction]);
  return <div className="gym-shell">
    <header className="gym-header"><div><span className="gym-kicker">HAX / GYM</span><h1>BFF Classic Training Arena</h1><p>Play a real 1v1 on the futsal-classic room map, record decisions, then train from your session.</p></div><span className="gym-status"><i className={running ? 'live' : ''} />{running ? 'LIVE MATCH' : 'PAUSED'}</span></header>
    <main className="gym-grid"><section className="gym-stage"><canvas ref={canvasRef} aria-label="BFF Classic 1v1 training pitch" /><div className="gym-controls"><button className="gym-primary" onClick={() => setRunning(value => !value)}>{running ? 'Pause match' : 'Start match'}</button><button onClick={reset}>Reset kickoff</button><button onClick={runPrediction}>Ask model</button><span>Move: WASD / arrows · Kick: Space</span></div></section>
      <aside className="gym-panel"><div className="gym-card"><span className="gym-kicker">MODEL OPPONENT</span><h2>BFF Classic 1v1</h2><p>The opponent uses the futsal-classic room geometry and native collision masks. It approaches the ball before kicking, so every touch is physically reachable.</p><small className="gym-engine-badge">NATIVE HAXBALL ENGINE · 60 HZ</small><label>Play style<select value={skill} onChange={event => setSkill(event.target.value)}><option value="balanced">Balanced</option><option value="pressing">Pressing</option><option value="conservative">Conservative</option></select></label><label>Render extrapolation <input type="range" min={-EXTRAPOLATION_LIMIT_MS} max={EXTRAPOLATION_LIMIT_MS} step="10" value={extrapolationMs} onChange={event => { const value = Number(event.target.value); setExtrapolationMs(value); localStorage.setItem(`${STORAGE_KEY}:extrapolationMs`, String(value)); }} /><span className="gym-range-value">{extrapolationMs > 0 ? `+${extrapolationMs}` : extrapolationMs} ms</span></label><small>Prediction affects the canvas only; fixed-tick physics and kicks stay authoritative.</small></div><div className="gym-card gym-score"><div><small>YOU</small><strong>{stats.you}</strong></div><span>:</span><div><small>MODEL</small><strong>{stats.ai}</strong></div></div><div className="gym-card"><span className="gym-kicker">LIVE COACH</span><h3>{bestText}</h3><p>{prediction?.best?.explanation || 'Pause with the ball and ask the model for a legal action prediction.'}</p>{prediction?.best && <small>Physics preview: {prediction.best.metrics?.setupCompleted === false ? 'setup still required' : 'executable'} · confidence {prediction.best.confidence ? `${Math.round(prediction.best.confidence * 100)}%` : 'estimated'}</small>}</div><div className="gym-card"><span className="gym-kicker">TRAINING LOG</span><div className="gym-metrics"><span><b>{stats.episodes}</b> episodes</span><span><b>{stats.examples + examplesRef.current.length}</b> examples</span><span><b>{stats.trained}</b> local fits</span></div><p>Policy v{policy.version || 0} · kick range {Math.round(policy.kickRange || 0)} · pursuit {(Number(policy.pursuit || 1)).toFixed(2)}×</p><button className="gym-primary" disabled={!examplesRef.current.length} onClick={train}>Train on this session</button><small>Training stays in this browser until the persistent Gym evaluator is connected.</small></div></aside></main>
  </div>;
}
